import { useEffect, useState } from 'react'

import { CircleInfo, TrashBin } from '@gravity-ui/icons'
import { Button, cn,Input, ListBox, Select, Switch, Table, Tooltip } from '@heroui/react'
import type { Control, UseFormRegister } from 'react-hook-form'
import { Controller, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { DateInput } from '@/components/ui/DateInput'
import { Country, COUNTRY_CURRENCY_MAP, Currency } from '@/consts/enums'
import { useRatesStore } from '@/store/rates.store'
import { calcPLN, formatPLN } from '@/utils/currency'
import { numericOnly } from '@/utils/input'

import { ExpenseAutocomplete } from './ExpenseAutocomplete'

interface ExpenseTableRowProps {
  index: number;
  fieldId: string;
  register: UseFormRegister<any>;
  control: Control<any>;
  setValue: (name: string, value: any, options?: any) => void;
  row: any;
  onRemove: (index: number) => void;
  onFetchRate: (index: number, currency: string, date: string) => void;
}

export function ExpenseTableRow({
                                  index,
                                  fieldId,
                                  control,
                                  setValue,
                                  row,
                                  onRemove,
                                  onFetchRate,
                                }: ExpenseTableRowProps) {
  const { loading: ratesLoading } = useRatesStore()
  const { t } = useTranslation()

  const regionNames = new Intl.DisplayNames(['ru'], { type: 'region' })

  const watchedRow = useWatch({
    control,
    name: `rows.${index}`,
    defaultValue: row,
  })

  const [amountInput, setAmountInput] = useState(() => {
    const val = watchedRow?.amount
    return val !== undefined && val !== null && val !== 0 ? String(val).replace('.', ',') : ''
  })

  useEffect(() => {
    const val = watchedRow?.amount
    const formatted = val !== undefined && val !== null && val !== 0 ? String(val).replace('.', ',') : ''
    const parsedLocal = Number(amountInput.replace(',', '.'))
    const parsedVal = val !== undefined && val !== null ? Number(val) : 0
    const isTransitionState = isNaN(parsedLocal)
    if (isTransitionState) {
      if (parsedVal !== 0) {
        setAmountInput(formatted)
      }
    } else if (parsedLocal !== parsedVal) {
      setAmountInput(formatted)
    }
  }, [watchedRow?.amount])


  const nbpLoading = !!ratesLoading[`${watchedRow?.currency?.toLowerCase()}_${watchedRow?.date}`]
  const plnVal = calcPLN(Number(watchedRow?.amount) || 0, Number(watchedRow?.exchangeRate) || 0)

  const isIncluded = watchedRow?.isIncluded !== false

  return (
    <Table.Row
      key={fieldId}
      className={cn(
        "transition-all duration-200",
        isIncluded
          ? "hover:bg-zinc-50/50 dark:hover:bg-zinc-800/30"
          : "bg-zinc-100/30 dark:bg-zinc-900/30 opacity-50"
      )}
    >
      <Table.Cell className="px-2 py-1.5 text-center">
        <Controller
          name={`rows.${index}.isIncluded`}
          control={control}
          render={({ field }) => (
            <Switch
              isSelected={field.value !== false}
              onChange={field.onChange}
              size="sm"
            >
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
            </Switch>
          )}
        />
      </Table.Cell>
      <Table.Cell className="px-2 py-1.5">
        <Controller
          name={`rows.${index}.date`}
          control={control}
          render={({ field, fieldState }) => (
            <DateInput
              {...field}
              className="w-64"
              variant="secondary"
              isInvalid={!!fieldState.error}
              errorMessage={fieldState.error?.message}
              onChange={val => {
                field.onChange(val)
                if (watchedRow?.currency !== 'PLN') {
                  onFetchRate(index, watchedRow?.currency, val)
                }
              }}
            />
          )}
        />
      </Table.Cell>
      <Table.Cell className="px-2 py-1.5 w-full">
        <Controller
          name={`rows.${index}.type`}
          control={control}
          render={({ field, fieldState }) => (
            <ExpenseAutocomplete
              {...field}
              placeholder={t('reports.table.type_placeholder')}
              variant="secondary"
              size="sm"
              isInvalid={!!fieldState.error}
              errorMessage={fieldState.error?.message}
              className={'min-w-72'}
            />
          )}
        />
      </Table.Cell>
      <Table.Cell className="px-2 py-1.5">
        <Controller
          name={`rows.${index}.documentNumber`}
          control={control}
          render={({ field, fieldState }) => (
            <Input
              {...(field as any)}
              placeholder={t('reports.table.doc_placeholder')}
              variant="secondary"
              isInvalid={!!fieldState.error}
              errorMessage={fieldState.error?.message}
            />
          )}
        />
      </Table.Cell>
      <Table.Cell className="px-2 py-1.5">
        <Controller
          name={`rows.${index}.country`}
          control={control}
          render={({ field, fieldState }) => (
            <Select
              className="w-full"
              selectedKey={field.value}
              onSelectionChange={key => {
                const val = key as string
                field.onChange(val)
                const mappedCurrency = COUNTRY_CURRENCY_MAP[val]
                if (mappedCurrency) {
                  setValue(`rows.${index}.currency`, mappedCurrency, { shouldValidate: true })
                  onFetchRate(index, mappedCurrency, watchedRow?.date)
                }
              }}
              variant={'secondary'}
              isInvalid={!!fieldState.error}
            >
              <Select.Trigger ref={field.ref}>
                <Select.Value>
                  {({ selectedText }) => selectedText}
                </Select.Value>
                <Select.Indicator />
              </Select.Trigger>
              <Select.Popover>
                <ListBox>
                  {Object.values(Country).map(code => (
                    <ListBox.Item key={code} id={code} textValue={code}>
                      <div className="flex flex-col">
                        <span>{code}</span>
                        <span className="text-xs text-default-400">{regionNames.of(code)}</span>
                      </div>
                    </ListBox.Item>
                  ))}
                </ListBox>
              </Select.Popover>
            </Select>
          )}
        />
      </Table.Cell>
      <Table.Cell className="px-2 py-1.5">
        <Controller
          name={`rows.${index}.amount`}
          control={control}
          render={({ field }) => (
            <Input
              ref={field.ref}
              name={field.name}
              onBlur={field.onBlur}
              value={amountInput}
              type="text"
              onKeyDown={numericOnly}
              placeholder="0.00"
              variant="secondary"
              onChange={e => {
                const valStr = e.target.value.replace('.', ',')
                setAmountInput(valStr)
                const parsedVal = Number(valStr.replace(',', '.'))
                const finalVal = isNaN(parsedVal) ? 0 : parsedVal
                field.onChange(finalVal)
                const rate = Number(watchedRow?.exchangeRate) || 1
                setValue(`rows.${index}.amountPLN`, calcPLN(finalVal, rate))
              }}
            />
          )}
        />
      </Table.Cell>
      <Table.Cell className="px-2 py-1.5">
        <Controller
          name={`rows.${index}.currency`}
          control={control}
          render={({ field, fieldState }) => (
            <Select
              className="w-full"
              selectedKey={field.value}
              onSelectionChange={key => {
                const val = key as string
                field.onChange(val)
                onFetchRate(index, val, watchedRow?.date)
              }}
              variant={'secondary'}
              isInvalid={!!fieldState.error}
            >
              <Select.Trigger ref={field.ref}>
                <Select.Value />
                <Select.Indicator />
              </Select.Trigger>
              <Select.Popover>
                <ListBox>
                  {Object.values(Currency).map(c => (
                    <ListBox.Item key={c} id={c} textValue={c}>
                      {c}
                    </ListBox.Item>
                  ))}
                </ListBox>
              </Select.Popover>
            </Select>
          )}
        />
      </Table.Cell>
      <Table.Cell className="px-2 py-1.5 text-right text-xs text-foreground/50">
        {nbpLoading ? (
          <div className="flex items-center justify-end py-2 pr-2 ">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="animate-spin">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" strokeOpacity="0.2" />
              <path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-1.5">
            {watchedRow?.nbpTable && watchedRow?.nbpTable !== '—' && <Tooltip delay={0}>
              <Button
                isIconOnly variant="tertiary" size="sm"
                className="w-5 h-5 min-w-0 p-0 opacity-60 hover:opacity-100"
              >
                <CircleInfo width={12} height={12} />
              </Button>
              <Tooltip.Content>
                <p>{watchedRow?.nbpTable}</p>
              </Tooltip.Content>
            </Tooltip>}
            <span className="font-bold">{Number(watchedRow?.exchangeRate || 0).toFixed(4)}</span>
          </div>
        )}
      </Table.Cell>
      <Table.Cell className="px-4 py-1.5 text-right font-bold min-w-30">
        {formatPLN(plnVal)}
      </Table.Cell>
      <Table.Cell className="px-2 py-1.5 text-center">
        <Tooltip delay={0}>
          <Button
            isIconOnly
            size="sm"
            variant="danger-soft"
            onClick={() => onRemove(index)}
          >
            <TrashBin className="size-4" />
          </Button>
          <Tooltip.Content>
            <p>{t('common.delete')}</p>
          </Tooltip.Content>
        </Tooltip>
      </Table.Cell>
    </Table.Row>
  )
}
